import { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';

import type { AnalyticsListOptions } from '../../../analytics/analyticsHandler';
import type { ApiPriceData, ApiProduct } from '../../../models/ecommerce/product/ApiProduct';

import cartItemsSelectors from '../../cart/selectors/cartItemsSelectors';
import { storeSelectors } from '../selectors/storeSelectors';
import { unsyncedCartItemsSelectors } from '../selectors/unsyncedCartItemsSelectors';
import { unsyncedCartItemsActions } from '../store/slices/unsyncedCartItemsSlice';
import { CncStep } from '../store/structureDefinitions/uiState';
import { cartThunks } from '../thunks/cartThunks';
import { sendGaModifyCartEvent } from '../tracking/gaEventCreators';
import { useAsyncDispatcher } from './useAsyncDispatcher';
import { buyableProductEventsTracking } from './useBuyableProductEventsTracking';
import { dispatchSetTo } from './useCncFlyInState';
import { useAppDispatch, useAppSelector } from './useThunkDispatch';
import { useIsUserAMember } from './useUserProfile';

const initialVariant = (productVariants: ProductDataVariant[]) => {
    if (productVariants && productVariants.length > 0) {
        return productVariants[0];
    }

    return null;
};

const useExistingCartItemState = (productId: string, variants: ProductDataVariant[]) => {
    const [currentVariant, setCurrentVariant] = useState(initialVariant(variants));

    const unsyncedCartItem = useAppSelector((state) =>
        unsyncedCartItemsSelectors.selectUnsyncedCartItemByUniqueId(state, {
            productId,
            variantId: currentVariant?.id,
        }),
    );

    const cartItem = useAppSelector((state) =>
        cartItemsSelectors.selectById(state, {
            productId,
            variantId: currentVariant?.id,
        }),
    );

    const handleVariantChange = (variantId: string) => {
        const variant = variants?.find((x) => x.id === variantId) || null;
        setCurrentVariant(variant);
    };

    useEffect(() => {
        setCurrentVariant(initialVariant(variants));
    }, [setCurrentVariant, variants, productId]);

    return {
        cartItem,
        unsyncedCartItem,
        currentVariant,
        handleVariantChange,
    };
};

export const useCartDispatchers = (options: UseBuyableProductStateOptions) => {
    const dispatch = useAppDispatch();
    const hasActiveStoreSelection = useAppSelector(storeSelectors.hasActiveStoreSelection);
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);
    const isUserMember = useIsUserAMember();

    const addToCartDispatcher = useAsyncDispatcher(
        async (
            _productId: string,
            _variantId: string | undefined,
            _quantity: number,
            _onAddToCart?: Function,
        ) => {
            if (!hasActiveStoreSelection) {
                dispatch(
                    unsyncedCartItemsActions.addUnsyncedCartItem({
                        productId: _productId,
                        variantId: _variantId,
                        quantity: _quantity,
                        analyticsOptions: options?.analyticsOptions,
                    }),
                );
                dispatch(dispatchSetTo({ step: CncStep.MiniCart }));
            } else {
                await dispatch(
                    cartThunks.updateQuantity({
                        productId: _productId,
                        variantId: _variantId,
                        quantity: _quantity,
                        silent: options?.isSilent,
                    }),
                );

                sendGaModifyCartEvent(
                    _productId,
                    _variantId,
                    _quantity,
                    0,
                    currentProductionUnit,
                    isUserMember,
                    options?.analyticsOptions,
                );

                if (_onAddToCart) _onAddToCart();
            }
        },
    );

    const updateQuantityDispatcher = useAsyncDispatcher(
        async (
            _productId: string,
            _variantId: string | undefined,
            _newQuantity: number,
            _previousQuantity: number,
            _onCartItemQuantityUpdate?: Function,
        ) => {
            await dispatch(
                cartThunks.updateQuantity({
                    productId: _productId,
                    variantId: _variantId,
                    quantity: _newQuantity,
                    silent: options?.isSilent,
                }),
            );

            sendGaModifyCartEvent(
                _productId,
                _variantId,
                _newQuantity,
                _previousQuantity,
                currentProductionUnit,
                isUserMember,
                options?.analyticsOptions,
            );

            if (_onCartItemQuantityUpdate) _onCartItemQuantityUpdate();
        },
    );

    const removeFromCartDispatcher = useAsyncDispatcher(
        async (
            _productId: string,
            _variantId: string | undefined,
            _previousQuantity: number,
            _onRemoveFromCart?: Function,
        ) => {
            await dispatch(
                cartThunks.updateQuantity({
                    productId: _productId,
                    variantId: _variantId,
                    quantity: 0,
                    silent: options?.isSilent,
                }),
            );

            sendGaModifyCartEvent(
                _productId,
                _variantId,
                0,
                _previousQuantity,
                currentProductionUnit,
                isUserMember,
                options?.analyticsOptions,
            );

            if (_onRemoveFromCart) _onRemoveFromCart();
        },
    );

    return {
        addToCartDispatcher,
        removeFromCartDispatcher,
        updateQuantityDispatcher,
    };
};

export const useBuyableState = (
    product: ApiProduct,
    options: UseBuyableProductStateOptions,
    onAddToCartDone?: (previousQuantity: number, newQuantity: number) => void,
    onQuantityUpdateDone?: (previousQuantity: number, newQuantity: number) => void,
    onRemoveFromCartDone?: (previousQuantity: number, newQuantity: number) => void,
) => {
    const { currentVariant, quantity, handleVariantChange, handleQuantityUpdate } =
        useBuyableProductState(product, {
            isSilent: options.isSilent,
            analyticsOptions: options.analyticsOptions,
        });

    const onQuantityUpdate = useCallback(
        (newQuantity: number) => {
            const addToCartDone = () => {
                buyableProductEventsTracking.onAddToCartTrack({
                    identifier: product.identifier,
                    priceData: product.priceData,
                    quantity: newQuantity,
                    previousQuantity: quantity,
                    promotionPriceData: product.promotionPriceData,
                });
                onAddToCartDone?.(0, newQuantity);
            };

            const removeFromCartDone = () => {
                buyableProductEventsTracking.onRemoveFromCartTrack({
                    identifier: product.identifier,
                    priceData: product.priceData,
                    quantity: newQuantity,
                    previousQuantity: quantity,
                    promotionPriceData: product.promotionPriceData,
                });
                onRemoveFromCartDone?.(quantity, newQuantity);
            };

            const quantityUpdateDone = () => {
                buyableProductEventsTracking.onQuantityUpdateTrack({
                    identifier: product.identifier,
                    priceData: product.priceData,
                    quantity: newQuantity,
                    previousQuantity: quantity,
                    promotionPriceData: product.promotionPriceData,
                });
                onQuantityUpdateDone?.(quantity, newQuantity);
            };

            handleQuantityUpdate(
                newQuantity,
                addToCartDone,
                quantityUpdateDone,
                removeFromCartDone,
            );
        },
        [
            onAddToCartDone,
            onQuantityUpdateDone,
            onRemoveFromCartDone,
            handleQuantityUpdate,
            product.identifier,
            product.priceData,
            product.promotionPriceData,
            quantity,
        ],
    );

    return {
        currentVariant,
        quantity,
        handleVariantChange,
        onQuantityUpdate,
    };
};

export const useBuyableProductState = (
    productData: ProductData,
    options: UseBuyableProductStateOptions,
) => {
    const { cartItem, unsyncedCartItem, currentVariant, handleVariantChange } =
        useExistingCartItemState(productData.identifier, productData.variants);

    const { addToCartDispatcher, removeFromCartDispatcher, updateQuantityDispatcher } =
        useCartDispatchers(options);

    const { execute: addToCartDispatcherExecute } = addToCartDispatcher;
    const { execute: removeFromCartDispatcherExecute } = removeFromCartDispatcher;
    const { execute: updateQuantityDispatcherExecute } = updateQuantityDispatcher;

    const handleQuantityUpdate = useMemo(
        () =>
            _.debounce(
                (
                    _newQuantity: number,
                    addToCartCallback?: () => void,
                    onQuantityUpdateCallback?: () => void,
                    onRemoveFromCartCallback?: () => void,
                ) => {
                    if (!cartItem) {
                        addToCartDispatcherExecute(
                            productData.identifier,
                            currentVariant?.id,
                            _newQuantity,
                            addToCartCallback,
                        );
                        return;
                    }

                    if (_newQuantity === 0) {
                        removeFromCartDispatcherExecute(
                            cartItem.productId,
                            cartItem.variantId,
                            cartItem.quantity,
                            onRemoveFromCartCallback,
                        );
                    } else {
                        updateQuantityDispatcherExecute(
                            cartItem.productId,
                            cartItem.variantId,
                            _newQuantity,
                            cartItem.quantity,
                            onQuantityUpdateCallback,
                        );
                    }
                },
                500,
            ),
        [
            addToCartDispatcherExecute,
            cartItem,
            currentVariant?.id,
            productData.identifier,
            removeFromCartDispatcherExecute,
            updateQuantityDispatcherExecute,
        ],
    );

    return {
        addToCartDispatcher,
        updateQuantityDispatcher,
        removeFromCartDispatcher,
        currentVariant,
        quantity: cartItem?.quantity || unsyncedCartItem?.quantity || 0,
        handleVariantChange,
        handleQuantityUpdate,
    };
};

interface UseBuyableProductStateOptions {
    isSilent: boolean;
    analyticsOptions: AnalyticsListOptions;
}

interface ProductDataVariant {
    id: string;
    name: string;
    priceData?: ApiPriceData;
    promotionPriceData?: ApiPriceData;
}

interface ProductData {
    identifier: string;
    variants: ProductDataVariant[];
}
